import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SiteModel } from '../site-model/site-model.types';
import { SITEMANAGER_BASE_PATH } from './sitemanager-base-path.token';

/**
 * Modèle 3D d'un site (ADR-017 point 7, flux 29, api-site 8.1.0). Appels écrits à la main tant que
 * `@wheere/api-site` 8.1.0 n'est pas publié : à remplacer par le `SiteModelService` généré.
 *
 * Le contenu est relu en `arraybuffer` (l'intercepteur pose le jeton) ; le serveur répond avec un
 * ETag et `no-cache` : le navigateur revalide et reçoit un 304 s'il a déjà ce GLB.
 */
@Injectable({ providedIn: 'root' })
export class SiteModelClient {
  readonly #http = inject(HttpClient);
  readonly #basePath = inject(SITEMANAGER_BASE_PATH);

  /** `GET /sites/{id}/model` ; `null` si le site n'a pas de modèle (404). */
  async get(siteId: string): Promise<SiteModel | null> {
    try {
      return await firstValueFrom(this.#http.get<SiteModel>(`${this.#basePath}/sites/${siteId}/model`));
    } catch (e) {
      if (e instanceof HttpErrorResponse && e.status === 404) {
        return null;
      }
      throw e;
    }
  }

  /**
   * `PUT /sites/{id}/model` (site:configure-files) : dépôt contrôlé du GLB ; remplace le modèle
   * en place. Refus 422 (`SiteModelRejection`) ou 413 (au-delà de 20 Mo) : l'erreur HTTP remonte,
   * lue par `siteModelRejection`.
   */
  upload(siteId: string, file: File): Promise<SiteModel> {
    const form = new FormData();
    form.append('file', file, file.name);
    return firstValueFrom(this.#http.put<SiteModel>(`${this.#basePath}/sites/${siteId}/model`, form));
  }

  /** `DELETE /sites/{id}/model` (site:configure-files) : 204, même si le site n'avait pas de modèle. */
  async delete(siteId: string): Promise<void> {
    await firstValueFrom(this.#http.delete(`${this.#basePath}/sites/${siteId}/model`));
  }

  /** `GET /sites/{id}/model/content` : le GLB. */
  content(siteId: string): Promise<ArrayBuffer> {
    return firstValueFrom(
      this.#http.get(`${this.#basePath}/sites/${siteId}/model/content`, { responseType: 'arraybuffer' })
    );
  }
}
